// CB:BEGIN homeowner-real-dashboard
/**
 * HomeownerRealDashboard — Admin view of a homeowner's dashboard.
 *
 * Shows EXACTLY what the homeowner sees: the NextActionBanner driven by live
 * project flags, and the P0ProjectFlow component for each project.
 * No custom status pane — we reuse the same components the homeowner uses.
 */
import React, { useState, useEffect } from 'react';
import { useApi } from '../../lib/useApi';
import { NextActionBanner } from '../NextActionBanner';
import { P0ProjectFlow } from '../P0ProjectFlow';
import { getStatusPill } from '../../styles/adminTokens';

interface HomeownerRealDashboardProps {
  homeownerId: string;
  hideHeader?: boolean;
}

interface ProjectStub {
  id: string;
  address: string;
  status: string;
  createdAt: string;
}

const HomeownerRealDashboard: React.FC<HomeownerRealDashboardProps> = ({ homeownerId }) => {
  const api = useApi();
  const [projects, setProjects] = useState<ProjectStub[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get(`/api/admin/homeowners/${homeownerId}`)
      .then((data: any) => {
        const list: ProjectStub[] = (data?.projects ?? []);
        setProjects(list);
      })
      .catch((err: any) => setError(err?.message ?? 'Failed to load projects'))
      .finally(() => setLoading(false));
  }, [homeownerId]);

  if (loading) {
    return (
      <div style={{ padding: '32px 24px', textAlign: 'center', color: '#6b7280' }}>
        Loading homeowner view…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px', color: '#dc2626', fontSize: 14 }}>
        {error}
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div style={{ padding: '40px 24px', textAlign: 'center', color: '#6b7280', fontSize: 14 }}>
        This homeowner has no projects yet.
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: 900 }}>
      {projects.map((project) => {
        const pill = getStatusPill(project.status);
        return (
          <div key={project.id} style={{ marginBottom: 32 }}>
            {/* Project header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{project.address}</div>
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
                  Created {new Date(project.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </div>
              </div>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                background: pill.bg, color: pill.text,
              }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: pill.dot, display: 'inline-block' }} />
                {pill.label}
              </span>
            </div>

            {/* NextActionBanner — exactly what the homeowner sees (no forRole filter) */}
            <div style={{ marginBottom: 14 }}>
              <NextActionBanner projectId={project.id} />
            </div>

            {/* P0ProjectFlow — same component the homeowner sees, role='homeowner' */}
            <P0ProjectFlow
              projectId={project.id}
              role="homeowner"
              onUpdated={() => {}}
            />
          </div>
        );
      })}
    </div>
  );
};

export default HomeownerRealDashboard;
// CB:END homeowner-real-dashboard
